import React, {useMemo} from 'react';
import {
	Avatar,
	Chip,
	Divider,
	Paper,
	Tooltip,
	Typography
} from "@mui/material";
import {useTheme} from "@mui/styles";
import moment from "moment";
import clsx from "clsx";
import AttachFileTwoToneIcon from "@mui/icons-material/AttachFileTwoTone";
import CheckCircleTwoToneIcon from "@mui/icons-material/CheckCircleTwoTone";
import ErrorTwoToneIcon from '@mui/icons-material/ErrorTwoTone';
import GetAppTwoToneIcon from '@mui/icons-material/GetAppTwoTone';
import LocalOfferTwoToneIcon from "@mui/icons-material/LocalOfferTwoTone";
import PersonTwoToneIcon from "@mui/icons-material/PersonTwoTone";
import SupportAgentTwoToneIcon from "@mui/icons-material/SupportAgentTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import {lightBlue, lightGreen, orange, purple} from "@mui/material/colors";
import {GET_ALLEGATO_ULR} from "../../../../services/utils/ApiUrls";
import {formatText} from "../../../widgets/chat/ChatFab";

export function getAllegatoById(idAllegato, forceDownload = false) {
	const url = GET_ALLEGATO_ULR + idAllegato;

	if (forceDownload) {
		const link = document.createElement('a');
		link.href = url;
		link.download = '';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	} else {
		window.open(url);
	}
}

const hiddenTags = [
	"#MOSTRA_USER",
	"#IN_ATTESA_DI_RISPOSTA",
	"#RICHIESTA_CHIAMATA",
	"#INOLTRATO",
];

const getCleanNote = (note = "") => hiddenTags.reduce(
	(currentNote, tag) => currentNote.replaceAll(tag, ""),
	String(note)
).trim();

const getTags = (note = "") => String(note)
	.match(/#(\S*)/g)
	?.filter((tag) => !hiddenTags.includes(tag))
	|| [];

const isUserHistoryItem = (item = {}) => {
	const lastModify = item.lastModify || "";
	const lowerLastModify = lastModify.toLowerCase();
	return lastModify === "Risposta Utente"
		|| lastModify === "Online-form"
		|| lowerLastModify.includes("richiesta utente");
};

const getEntryTitle = (item = {}) => {
	const lastModify = item.lastModify || "Operatore";
	const lowerLastModify = lastModify.toLowerCase();

	if (lastModify === "Risposta Utente") {
		return "Risposta dell'utente";
	}
	if (lowerLastModify.includes("richiesta utente")) {
		return "Richiesta utente tramite call center";
	}
	if (lastModify === "Online-form") {
		return "Richiesta da form online";
	}
	return lastModify;
};

const getEntryKicker = (item = {}) => {
	if (item.stato === "Risolto") {
		return "Chiusura";
	}
	return isUserHistoryItem(item) ? "Utente" : "Operatore";
};

const getEntryColor = (item = {}) => {
	if (item.stato === "Risolto") return "#34a853";
	if (isUserHistoryItem(item)) return "#f57c00";
	return "#536dfe";
};

const getEntryIcon = (item = {}) => {
	if (item.stato === "Risolto") return <CheckCircleTwoToneIcon/>;
	if (isUserHistoryItem(item)) return <PersonTwoToneIcon/>;
	return <SupportAgentTwoToneIcon/>;
};

const getNoteContent = (item, useStringify) => {
	if (useStringify) {
		try {
			return JSON.parse(item.note)?.dettaglioRichiesta || "";
		} catch (error) {
			return "";
		}
	}

	return formatText(getCleanNote(item.note));
};

const getSortedHistory = (storicoTicket = []) => [...storicoTicket]
	.sort((a, b) => new Date(a.dataUltimaModifica) - new Date(b.dataUltimaModifica));

const renderAttachmentChips = (item, chipClassName) => {
	if (!item.allegato) return null;

	const attachments = item.allegato.split(" ").filter(Boolean);

	return (
		<React.Fragment>
			<Tooltip title="Scarica tutti gli allegati presenti">
				<Chip
					clickable
					size="small"
					className={chipClassName}
					icon={<GetAppTwoToneIcon/>}
					label="Scarica tutti"
					onClick={(event) => {
						event.stopPropagation();
						attachments.forEach((file) => getAllegatoById(file, true));
					}}
				/>
			</Tooltip>
			{attachments.map((allegato, index) => (
				<Tooltip key={allegato + index} title="Visualizza anteprima allegato">
					<Chip
						clickable
						size="small"
						className={chipClassName}
						icon={<AttachFileTwoToneIcon/>}
						label={`Allegato ${index + 1}`}
						onClick={() => getAllegatoById(allegato)}
					/>
				</Tooltip>
			))}
		</React.Fragment>
	);
};

const TimelineHistory = ({items, ticketClasses, useStringify}) => {
	if (!items.length) {
		return (
			<div className={ticketClasses.historyEmpty}>
				<HistoryEmptyIcon/>
				<Typography component="h3">Nessun movimento registrato</Typography>
				<Typography>Lo storico del ticket verra mostrato qui appena disponibile.</Typography>
			</div>
		);
	}

	return (
		<div className={ticketClasses.historyTimeline}>
			{items.map((item, index) => {
				const visibleTags = getTags(item.note);
				const hasVisibleNote = item.note?.includes("#MOSTRA_USER");
				const noteContent = getNoteContent(item, useStringify);
				const entryDate = item.dataCreazione
					? moment(item.dataCreazione).format("DD/MM/YYYY - HH:mm")
					: "Data non disponibile";

				return (
					<div
						key={`${item.dataCreazione || index}-${item.lastModify || "history"}`}
						className={ticketClasses.historyItem}
						style={{"--history-color": getEntryColor(item)}}
					>
						<div className={ticketClasses.historyRail}>
							<Avatar className={ticketClasses.historyDot}>
								{getEntryIcon(item)}
							</Avatar>
							{index < items.length - 1 && <span className={ticketClasses.historyLine}/>}
						</div>

						<div className={ticketClasses.historyEntry}>
							<div className={ticketClasses.historyEntryHeader}>
								<Typography component="span" className={ticketClasses.historyEntryKicker}>
									{getEntryKicker(item)}
								</Typography>
								<Typography component="h3" className={ticketClasses.historyEntryTitle}>
									{getEntryTitle(item)}
								</Typography>
								<Typography component="span" className={ticketClasses.historyEntryMeta}>
									{entryDate}
								</Typography>
							</div>

							{noteContent && (
								<Typography component="div" className={ticketClasses.historyEntryNote}>
									{noteContent}
								</Typography>
							)}

							{(hasVisibleNote || visibleTags.length > 0 || item.allegato || item.stato === "Risolto") && (
								<div className={ticketClasses.historyBadges}>
									{hasVisibleNote && (
										<Tooltip title="Il richiedente visualizza questa nota">
											<Chip
												size="small"
												className={ticketClasses.historyChip}
												icon={<VisibilityTwoToneIcon/>}
												label="Visibile"
											/>
										</Tooltip>
									)}
									{visibleTags.map((tag) => (
										<Chip
											key={tag}
											size="small"
											className={ticketClasses.historyChip}
											icon={tag === "#SOLLECITO" ? <ErrorTwoToneIcon/> : <LocalOfferTwoToneIcon/>}
											label={tag.substring(1)}
										/>
									))}
									{renderAttachmentChips(item, ticketClasses.historyChip)}
									{item.stato === "Risolto" && (
										<div className={ticketClasses.historyResolvedRow}>
											<Chip
												size="small"
												className={clsx(ticketClasses.historyChip, ticketClasses.historyResolvedChip)}
												icon={<CheckCircleTwoToneIcon/>}
												label="Ticket risolto"
											/>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};

const HistoryEmptyIcon = () => (
	<Avatar>
		<SupportAgentTwoToneIcon/>
	</Avatar>
);

const LegacyHistory = ({items, theme, useStringify}) => (
	<React.Fragment>
		{items.map((item, index) => {
			const isUserItem = isUserHistoryItem(item);
			const backgroundColor = isUserItem
				? item.lastModify?.toLowerCase().includes("richiesta utente")
					? (theme.palette.mode === "light" ? orange[50] : orange[800])
					: (theme.palette.mode === "light" ? lightBlue[50] : lightBlue[800])
				: (theme.palette.mode === "light" ? purple[50] : purple[800]);

			return (
				<Paper
					elevation={0}
					key={`${item.dataCreazione || index}-${item.lastModify || "history"}`}
					id={"paper-storico" + index}
					style={{
						backgroundColor,
						borderRadius: 24,
						padding: 16,
						marginLeft: isUserItem ? 0 : 100,
						marginRight: isUserItem ? 100 : 0,
						marginTop: 16,
					}}
				>
					<Typography variant="subtitle2" color="textSecondary">
						{isUserItem ? (
							<React.Fragment>
								<b>{getEntryTitle(item)}</b> in data: {moment(item.dataCreazione).format("DD/MM/YYYY - HH:mm")}
							</React.Fragment>
						) : (
							<React.Fragment>
								<b>{item.lastModify}</b> in data: {moment(item.dataCreazione).format("DD/MM/YYYY - HH:mm")}
							</React.Fragment>
						)}
					</Typography>
					<Typography variant="subtitle1">
						{getNoteContent(item, useStringify)}
					</Typography>

					{item.note?.includes("#MOSTRA_USER") && (
						<Tooltip title="Il richiedente visualizza questa nota">
							<Chip
								label="VISIBILE"
								avatar={<Avatar style={{backgroundColor: lightGreen[700]}}><VisibilityTwoToneIcon style={{color: "#FFF", fontSize: 18}}/></Avatar>}
								style={{marginRight: 8, marginTop: 8, backgroundColor: lightGreen[500], color: "#000"}}
							/>
						</Tooltip>
					)}

					{getTags(item.note).map((tag) => (
						<Chip
							key={tag}
							label={tag.substring(1)}
							avatar={<Avatar>{tag === "#SOLLECITO" ? <ErrorTwoToneIcon/> : "#"}</Avatar>}
							style={{marginRight: 8, marginTop: 8}}
						/>
					))}

					{renderAttachmentChips(item)}

					{item.stato === "Risolto" && (
						<React.Fragment>
							<Divider style={{marginTop: 16, marginBottom: 8}}/>
							<Typography variant="subtitle1" align="center">
								<b>TICKET Risolto</b>
							</Typography>
						</React.Fragment>
					)}
				</Paper>
			);
		})}
	</React.Fragment>
);

const StoricoTicket = ({storicoTicket = [], useStringify = false, variant = "legacy", ticketClasses}) => {
	const theme = useTheme();
	const sortedHistory = useMemo(() => getSortedHistory(storicoTicket), [storicoTicket]);

	if (variant === "timeline" && ticketClasses) {
		return (
			<TimelineHistory
				items={sortedHistory}
				ticketClasses={ticketClasses}
				useStringify={useStringify}
			/>
		);
	}

	return <LegacyHistory items={sortedHistory} theme={theme} useStringify={useStringify}/>;
};

export default React.memo(StoricoTicket);
